// 主人公の歩き 5 版目 (2026-10-13・DEV ?lunaWalk=v5) の脚と腰。 配布版は変えない。
//   4 版目の歩き v4walk (110px/秒) の答え: 「スピードはそこまで悪くないんだけど、膝？あたりで関節がうまくハマってないみたいにカクカクしてる」。
//   走りの答え: 「トテトテ歩きって感じだな。もう少しかっこいい寄りの歩き。重み？がいるのかな」。 ふだんの移動は歩き (110) に決まった。
//   記録 = docs/research/2026-10-13-lunaire-walk-v5.md
//
//   カクつきの元 (測った): 4 版目は「腰の高さ」を鍵で決め、膝は足首へ届くよう計算 (IK) で出していた。 床の足はほぼ伸び切っていて、
//   伸び切りの近くでは腰が 0.2px 動くだけで膝が 0↔10° 跳ぶ (膝の角度は「余り」の平方根で動く)。 さらに腰を届く高さへ切り詰める所 (hipClamp) で
//   膝が真っ直ぐに張り付き、爪先が離れる瞬間に 2 コマで 28° 折れた (1 コマ 21°)。 着地では腰が低いまま着くので膝が 30° 曲がって着き、
//   腰が 2 コマで上がる間に真っ直ぐへ戻った (1 コマ 15°)。
//
//   5 版目の作り: 向きを逆にする。 床の足は「膝の曲げ」を鍵で決め (人の歩きの膝の曲がり方: 着地の後に体重を受けて少し沈む・爪先が離れる前に曲がり始める)、
//   腰の高さはそこから計算で出す (どの足の膝も鍵より伸びない一番高い腰)。 その腰を、前後の一番低い所へ寄せてから丸く均す (hipTable)。
//   腰がなめらかで、膝が伸び切りから離れている (どこでも 9° 以上曲がっている) ので、膝 (= 腰と足首から計算) もなめらかに動く。
//   足首は 4 版目と同じ「床の足の転がり」(支点が床に留まる = 足が滑らない) だが、足の角度の鍵の間を区間ごとの smoothstep でなく
//   行き過ぎない曲線でつなぐ (鍵のたびに足の回りが止まると足首の速さが波打ち、伸び切り近くの膝を揺らした)。 爪先が離れる瞬間も足の回りを止めない。
//   浮いている足は 4 版目と同じ鍵の作り。 届く長さで切る代わりに、膝が伸び切る手前でなめらかに付け根へ寄せる。
//
//   このファイルは描画にもゲームの判定にも使う = Spine を import しない (テストから読める)。
import {
  angFromDown, LEG, maxHipForAnkle, periodicSpline, solveKnee, thighAt, unitsToWorld, worldToUnits,
  type Key, type LegGeom, type V4Gait, type V4LegState, type V4Pose,
} from './player_walk_v4'

export interface V5Gait extends V4Gait {
  /** 床に居る間の膝の曲げ (度・0 = 真っ直ぐ)。 s = 床に居る間の位置 0 (着地) 〜 1 (爪先が離れる)。 鍵の間は滑らかな曲線 */
  stanceKnee: readonly Key[]
  /** 腰の高さの上限 (Spine 単位・立ち姿 = 0)。 立ち姿より上へ伸び上がらない */
  hipCeil: number
  /** 腰を均す幅 (1周の割合・前後それぞれ)。 広いほど腰の動きが丸く、少し低くなる */
  hipWindow: number
  /**
   * 6 版目 c だけ (2026-10-13・src/player_walk_v6.ts)：腰の前後の揺れ (画面 px・+ = 前)。 1歩の中の位置 ψ (0 = 着地) の鍵を周期の曲線でつなぐ。
   *   着地で体がわずかに遅れ、床の足の上を越えて前へ乗る (重心の乗り移り)。 足首は床に留めたまま腰だけが動き、膝はそれに合わせて計算で出る。
   *   無い時 (5 版目・6 版目 a/b) は 0 で、計算は 5 版目と同じ。
   */
  hipSurge?: readonly Key[]
}

const wrap01 = (p: number) => ((p % 1) + 1) % 1

/** 端の傾きを 0 にした Hermite (鍵 t 昇順・0〜1)。 鍵の間で行き過ぎない (傾きは前後の鍵の小さい方に合わせる・単調な区間は単調のまま) */
function monoSpline(keys: readonly Key[], t: number, endSlope = false): number {
  const n = keys.length
  if (t <= keys[0]!.t) return keys[0]!.v + (endSlope ? (t - keys[0]!.t) * (keys[1]!.v - keys[0]!.v) / (keys[1]!.t - keys[0]!.t) : 0)
  if (t >= keys[n - 1]!.t) return keys[n - 1]!.v + (endSlope ? (t - keys[n - 1]!.t) * (keys[n - 1]!.v - keys[n - 2]!.v) / (keys[n - 1]!.t - keys[n - 2]!.t) : 0)
  let i = 0
  while (i < n - 2 && t > keys[i + 1]!.t) i++
  const d = (k: number) => (keys[k + 1]!.v - keys[k]!.v) / (keys[k + 1]!.t - keys[k]!.t)
  const tan = (k: number) => {
    // 端は止めずに、隣の区間の傾きのまま抜ける (爪先が離れる瞬間に足の回りが止まると、足首が一瞬戻って膝が伸び直す)
    if (k === 0) return endSlope ? d(0) : 0
    if (k === n - 1) return endSlope ? d(n - 2) : 0
    const a = d(k - 1), b = d(k)
    if (a * b <= 0) return 0                       // 山・谷では止める (行き過ぎない)
    return 2 / (1 / a + 1 / b)                     // 調和平均 (Fritsch–Carlson の簡単な形)
  }
  const k0 = keys[i]!, k1 = keys[i + 1]!
  const h = k1.t - k0.t, s = (t - k0.t) / h, s2 = s * s, s3 = s2 * s
  return (2 * s3 - 3 * s2 + 1) * k0.v + (s3 - 2 * s2 + s) * h * tan(i) + (-2 * s3 + 3 * s2) * k1.v + (s3 - s2) * h * tan(i + 1)
}

/** 膝の曲げ (度) から、付け根と足首の間の長さ (px) */
function reachForBend(L: LegGeom, bendDeg: number): number {
  const c = Math.cos(bendDeg * Math.PI / 180)
  return Math.sqrt(L.l1 * L.l1 + L.l2 * L.l2 + 2 * L.l1 * L.l2 * c)
}
/** 太ももの付け根 (画面 px)。 腰の translate が (xu, yu) (Spine 単位) の時。 xu = 0 なら thighAt と同じ */
function thighAtXY(L: LegGeom, xu: number, yu: number) {
  if (xu === 0) return thighAt(L, yu)
  const d = unitsToWorld(xu, yu)
  return { x: L.thigh.x + d.x, y: L.thigh.y + d.y }
}
/** 腰の前後 (Spine 単位の translate x)。 位相 qFront = 手前の足の位相。 hipSurge が無ければ 0 */
export function v5HipX(G: V5Gait, qFront: number): number {
  if (!G.hipSurge) return 0
  const px = periodicSpline(G.hipSurge, wrap01(2 * qFront))
  return worldToUnits(px, 0).x
}
/** 足首 a に対して、付け根との長さが D になる腰 (Spine 単位)。 付け根が足首より上にある側の解 (二分法) */
function hipForReach(L: LegGeom, a: { x: number, y: number }, D: number, xu = 0): number {
  let lo = -600, hi = 400
  for (let i = 0; i < 44; i++) {
    const mid = (lo + hi) / 2
    const o = thighAtXY(L, xu, mid)
    if (Math.hypot(o.x - a.x, o.y - a.y) <= D) lo = mid
    else hi = mid
  }
  return lo
}
const rad = (d: number) => d * Math.PI / 180
/** 画面で時計回り (右向きの人形で + = 爪先が下がる) に deg 回す */
const rot = (v: { x: number, y: number }, deg: number) => {
  const c = Math.cos(rad(deg)), s = Math.sin(rad(deg))
  return { x: c * v.x + s * v.y, y: -s * v.x + c * v.y }
}
const footKeysCache = new WeakMap<V5Gait, Key[]>()
/** 床に居る間の足の角度 (度)。 4 版目は鍵の間を区間ごとの smoothstep でつないでいて、鍵のたびに足の回りが止まった
 *  (= 足首の速さが波打ち、伸び切り近くの膝を揺らした)。 5 版目は鍵を通る行き過ぎない曲線でつなぐ */
function stanceFootV5(G: V5Gait, s: number): number {
  let k = footKeysCache.get(G)
  if (!k) { k = G.stance.map((x) => ({ t: x.s, v: x.foot })); footKeysCache.set(G, k) }
  return monoSpline(k, s, true)
}
/** 床に居る間の足首 (画面 px・体に対して)。 s = 0〜1 (負 = 着地の手前へ延ばした仮の位置)。 支点 (踵か爪先) は床に留まる */
export function stanceAnkleV5(G: V5Gait, L: LegGeom, s: number): { x: number, y: number, foot: number } {
  const sweep = G.speedPxPerSec * G.stanceFrac * 2 * G.stepSec
  const flatX = L.ankle.x + G.contactFwdPx - sweep * s
  const a = stanceFootV5(G, s)
  const pivotOff = a >= 0 ? L.toe : L.heel
  const pivot = { x: flatX + pivotOff.x, y: L.ankle.y + pivotOff.y }
  const back = rot({ x: -pivotOff.x, y: -pivotOff.y }, a)
  return { x: pivot.x + back.x, y: pivot.y + back.y, foot: a }
}
/** 端の傾きを決めた鍵の列 (t 昇順・端を含む) を Hermite でつなぐ */
function openSpline(ts: number[], vs: number[], m0: number, m1: number, t: number): number {
  const n = ts.length
  let i = 0
  while (i < n - 2 && t > ts[i + 1]!) i++
  const tan = (k: number) => (k === 0 ? m0 : k === n - 1 ? m1 : (vs[k + 1]! - vs[k - 1]!) / (ts[k + 1]! - ts[k - 1]!))
  const h = ts[i + 1]! - ts[i]!, u = (t - ts[i]!) / h, u2 = u * u, u3 = u2 * u
  return (2 * u3 - 3 * u2 + 1) * vs[i]! + (u3 - 2 * u2 + u) * h * tan(i) + (-2 * u3 + 3 * u2) * vs[i + 1]! + (u3 - u2) * h * tan(i + 1)
}
/** 鍵の太もも・膝の角度から足首の位置 (画面 px) */
function fk(L: LegGeom, o: { x: number, y: number }, thigh: number, knee: number) {
  const k = { x: o.x + L.l1 * Math.sin(rad(thigh)), y: o.y - L.l1 * Math.cos(rad(thigh)) }
  const sh = thigh - knee
  return { x: k.x + L.l2 * Math.sin(rad(sh)), y: k.y - L.l2 * Math.cos(rad(sh)) }
}
/**
 * 浮いている間の足首と足の角度。 u = 0 (爪先が離れた) 〜 1 (次に着く)。 端は床の姿と位置も速さもつながる (4 版目と同じ作り)。
 *   4 版目は曲線が脚の届く外へふくらんだ所を「届く長さで切る」で止めていた (膝が真っ直ぐに張り付く)。
 *   5 版目は膝の曲げが SWING_MIN_BEND° を切りそうな所だけ、なめらかに付け根へ寄せる (切り口が出ない)。
 */
function swingAnkleV5(G: V5Gait, L: LegGeom, u: number, hipY: number, dir: 1 | -1, xu = 0) {
  const o = thighAtXY(L, xu, hipY)
  const eps = 1e-3
  const end0 = stanceAnkleV5(G, L, 1), end0b = stanceAnkleV5(G, L, 1 - eps)
  const end1 = stanceAnkleV5(G, L, 0), end1b = stanceAnkleV5(G, L, eps)
  const k = (1 - G.stanceFrac) / G.stanceFrac
  const m0 = { x: (end0.x - end0b.x) / eps * k, y: (end0.y - end0b.y) / eps * k }
  const m1 = { x: (end1b.x - end1.x) / eps * k, y: (end1b.y - end1.y) / eps * k }
  const sc = dir === 1 ? 1 : G.backSwingScale
  const keys = G.swing.map((s) => ({ u: s.u, a: fk(L, o, s.thigh * sc, s.knee * sc), pf: s.pf * sc }))
  const ts = [0, ...keys.map((s) => s.u), 1]
  const ankle = {
    x: openSpline(ts, [end0.x, ...keys.map((s) => s.a.x), end1.x], m0.x, m1.x, u),
    y: openSpline(ts, [end0.y, ...keys.map((s) => s.a.y), end1.y], m0.y, m1.y, u),
  }
  const dx = ankle.x - o.x, dy = ankle.y - o.y, d = Math.hypot(dx, dy)
  const R0 = reachForBend(L, SWING_MIN_BEND + 8), R1 = reachForBend(L, SWING_MIN_BEND)
  if (d > R0) {
    const dd = R0 + (R1 - R0) * Math.tanh((d - R0) / (R1 - R0))
    ankle.x = o.x + dx * dd / d; ankle.y = o.y + dy * dd / d
  }
  const shinAt = (a: { x: number, y: number }) => angFromDown(solveKnee(L, o, a).knee, a)
  const shin = shinAt(ankle)
  const pf0 = end0.foot + (shinAt(end0) - L.shin0)
  const pf1 = end1.foot + (shinAt(end1) - L.shin0)
  const pf = openSpline(ts, [pf0, ...keys.map((s) => s.pf), pf1], 0, 0, u)
  return { ankle, foot: -(shin - L.shin0) + pf }
}
/** 浮いている足の膝が、これより伸びない (度) */
const SWING_MIN_BEND = 6

export function v5CycleSec(G: V5Gait): number { return 2 * G.stepSec }
export function v5StepPx(G: V5Gait): number { return G.speedPxPerSec * G.stepSec }

/** 床に居る足が決める腰 (Spine 単位)。 s = 床に居る間の位置 */
function stanceHip(G: V5Gait, L: LegGeom, s: number, xu = 0): number {
  const st = stanceAnkleV5(G, L, s)
  return hipForReach(L, st, reachForBend(L, monoSpline(G.stanceKnee, s)), xu)
}

/**
 * 5 版目の脚と腰 (位相 phase 0〜1・1周で2歩)。 dir = 1 前へ・−1 後ろへ (時間を逆に流し、浮いている足の振りを backSwingScale へ)。
 *   位相 0 = 手前の足 (front) が着いた瞬間。 奥の足 (back) は半周ずれる。
 */
export function v5Pose(G: V5Gait, phase: number, dir: 1 | -1 = 1): V4Pose & { hipX: number } {
  const p = wrap01(dir === 1 ? phase : -phase)
  const qs = { front: p, back: wrap01(p + 0.5) }
  const hip = v5Hip(G, qs.front)
  const xu = v5HipX(G, qs.front)
  return { front: legAt(G, LEG.front, qs.front, hip, dir, xu), back: legAt(G, LEG.back, qs.back, hip, dir, xu), hipY: hip, hipClamp: 0, hipX: xu }
}


/** 腰の高さ (Spine 単位)。 位相 qFront = 手前の足の位相 (0 = 着地)。 hipTable の表をなめらかにつなぐ */
export function v5Hip(G: V5Gait, qFront: number): number {
  const T = hipTable(G)
  const n = T.length
  const x = wrap01(qFront) * n
  const i = Math.floor(x), t = x - i
  const p0 = T[(i - 1 + n) % n]!, p1 = T[i % n]!, p2 = T[(i + 1) % n]!, p3 = T[(i + 2) % n]!
  // 周期の Catmull-Rom (表の点を必ず通る・なめらか)
  return 0.5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t + (-p0 + 3 * p1 - 3 * p2 + p3) * t * t * t)
}

/** 1周を何点で表にするか */
const HIP_N = 720
const hipCache = new WeakMap<V5Gait, Float64Array>()
/**
 * 腰の高さの表 (1周 HIP_N 点・Spine 単位)。
 *   ① 各点で、床に居る足それぞれの「膝を鍵の曲げにした時の腰」の一番低いもの (= どの足も膝が鍵より伸びない上限)。 立ち姿より上には行かない。
 *   ② その上限を、前後 HIP_WIN の中の一番低い値へ下げる (着地の少し前から、着く足に合わせて腰を下げ始める)。
 *   ③ 同じ幅で丸く均す。 均す幅が ② の幅を超えないので、均した後も ① の上限を超えない (= 膝はどこでも鍵の曲げ以上に曲がっている)。
 *   腰がなめらかで、膝が伸び切りから離れているので、膝 (= 腰と足首から計算) もなめらかに動く。
 */
function hipTable(G: V5Gait): Float64Array {
  const hit = hipCache.get(G)
  if (hit) return hit
  const N = HIP_N, f = G.stanceFrac
  const lim = new Float64Array(N)
  for (let i = 0; i < N; i++) {
    const p = i / N
    let h = G.hipCeil
    for (const [side, q] of [['front', p], ['back', wrap01(p + 0.5)]] as const) {
      if (q < f) h = Math.min(h, stanceHip(G, LEG[side], q / f, v5HipX(G, p)))
    }
    lim[i] = h
  }
  const r = Math.max(1, Math.round(G.hipWindow * N))
  const ero = new Float64Array(N)
  for (let i = 0; i < N; i++) {
    let m = Infinity
    for (let j = -r; j <= r; j++) m = Math.min(m, lim[(i + j + N) % N]!)
    ero[i] = m
  }
  const w: number[] = []
  let sum = 0
  for (let j = -r; j <= r; j++) { const v = 0.5 + 0.5 * Math.cos(Math.PI * j / (r + 1)); w.push(v); sum += v }
  const out = new Float64Array(N)
  for (let i = 0; i < N; i++) {
    let acc = 0
    for (let j = -r; j <= r; j++) acc += w[j + r]! * ero[(i + j + N) % N]!
    out[i] = acc / sum
  }
  hipCache.set(G, out)
  return out
}

function legAt(G: V5Gait, L: LegGeom, q: number, hipY: number, dir: 1 | -1, xu = 0): V4LegState {
  const f = G.stanceFrac
  const o = thighAtXY(L, xu, hipY)
  let ankle: { x: number, y: number }, foot: number
  const stance = q < f
  if (stance) {
    const st = stanceAnkleV5(G, L, q / f)
    ankle = { x: st.x, y: st.y }; foot = st.foot
  } else {
    const sw = swingAnkleV5(G, L, (q - f) / (1 - f), hipY, dir, xu)
    ankle = sw.ankle; foot = sw.foot
  }
  const { knee } = solveKnee(L, o, ankle)
  const w = worldToUnits(ankle.x - L.ankle.x, ankle.y - L.ankle.y)
  return { ik: w, foot, stance, ankle, knee, thigh: o }
}

/** 測る用: 床の足が届く一番高い腰 (Spine 単位・余り 0.1px) と、膝を鍵の曲げにした時の腰。 s = 床の間の位置 (負 = 着地の手前) */
export function v5Debug(G: V5Gait, side: 'front' | 'back', s: number) {
  const L = LEG[side]
  const a = stanceAnkleV5(G, L, s)
  return { reachMax: maxHipForAnkle(L, a, 0.1), keyHip: stanceHip(G, L, s), ankle: a }
}

/** 膝の曲げ (度・0 = 真っ直ぐ) を姿から測る (付け根→膝 と 膝→足首 の向きの差) */
export function kneeBendDeg(st: V4LegState): number {
  const a1 = Math.atan2(st.knee.x - st.thigh.x, -(st.knee.y - st.thigh.y))
  const a2 = Math.atan2(st.ankle.x - st.knee.x, -(st.ankle.y - st.knee.y))
  return (a1 - a2) * 180 / Math.PI
}

/**
 * v5: 歩き 110px/秒。 見本 = Z3 (ZZZ 刀の少女・1歩 27〜28 コマ = 0.45〜0.47 秒)・Z2 (黒い外套の女性・落ち着いた歩き)。
 *   床の膝 (stanceKnee) は人の歩きの膝の曲がり方 (着地 5° → 体重を受けて 15° ほど沈む → 片足の真上で伸びる → 踵が上がると曲がり始め、爪先が離れる時 35°) を写した。
 *   着地の後の膝の沈みが「体重が乗る」所。 1歩は 0.47 秒 (4 版目 0.45 秒・Z3 の遅い方に寄せて歩数を減らす)。
 */
export const V5_WALK: V5Gait = {
  speedPxPerSec: 110,
  stepSec: 0.45,
  stanceFrac: 0.57,
  contactFwdPx: 20,
  // 踵から着き (爪先が上・4 版目 −14° より浅く、つま先を跳ね上げない)、足の裏を着けて、踵を上げて爪先で蹴る
  stance: [{ s: 0, foot: -11 }, { s: 0.15, foot: 0 }, { s: 0.5, foot: 0 }, { s: 0.75, foot: 12 }, { s: 0.9, foot: 30 }, { s: 1, foot: 52 }],
  stanceKnee: [{ t: 0, v: 12 }, { t: 0.2, v: 19 }, { t: 0.5, v: 12 }, { t: 0.75, v: 13 }, { t: 1, v: 30 }],
  // 浮いている間: 爪先が離れた後に膝が一番曲がり (Z3 #8 膝 60°)、すれ違いの後に伸ばして、着地の手前でほぼ伸びる
  swing: [
    { u: 0.22, thigh: -2, knee: 56, pf: 14 },
    { u: 0.45, thigh: 14, knee: 46, pf: 4 },
    { u: 0.70, thigh: 25, knee: 22, pf: -4 },
    { u: 0.88, thigh: 23, knee: 14, pf: -9 },
  ],
  hip: [],
  hipCeil: 0,
  hipWindow: 0.06,
  backSwingScale: 0.8,
}
